import { useEffect, useState } from 'react';
import { Title, useCreatePath } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { FEATURE_CS_LIGHT, fetchCsQueue, toErrorMessage, type CsQueue, type CsQueueFilter } from './csWorkApi';

const CRITICALITY_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  urgent: 'error',
  high: 'warning',
  normal: 'default',
};

/**
 * Identyfikator odnośnika sprawy z kontraktu `data-testid` paczki (S-22.10-02): zmiana zlecenia
 * otwiera „tryb zmiany” (u nas karta zlecenia z sekcją zmian), prezlecenie — jego kartę.
 */
export function caseLinkTestId(c: { kind: string; target: { resource: string } }): string | undefined {
  if (c.kind === 'order_change') return 'cs-skrzynka-tryb-zmiany'; // testId zmiany zlecenia
  if (c.target.resource === 'pre-orders') return 'cs-skrzynka-otworz-prezlecenie'; // testId prezlecenia
  return undefined;
}

/**
 * Kolejka CS (paczka p. Mariusza 21.10, BR-CS-11; przełącznik `csLight`,
 * S-21.10-16): sprawy CS po terminie i krytyczności — prezlecenia do
 * uzupełnienia i zwrócone z pytaniem, uwagi od FTL, zmiany zleceń do decyzji
 * CS — z filtrami po kliencie, lokalizacji, rodzaju sprawy i wykonawcy.
 * Kolejność i krytyczność liczy API (`cs-work/cs-work.ts`); progi to
 * wartości robocze.
 */
export const CsQueueScreen = () => {
  const on = useFeatureState(FEATURE_CS_LIGHT);
  const createPath = useCreatePath();
  const [filter, setFilter] = useState<CsQueueFilter>({});
  const [queue, setQueue] = useState<CsQueue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (on !== true) return;
    let alive = true;
    // Krótkie opóźnienie przy pisaniu w polach tekstowych — jedno zapytanie.
    const t = setTimeout(() => {
      fetchCsQueue(filter)
        .then((q) => {
          if (!alive) return;
          setQueue(q);
          setError(null);
        })
        .catch((e) => {
          if (alive) setError(toErrorMessage(e));
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [on, filter]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  const set = (key: keyof CsQueueFilter) => (e: { target: { value: string } }) =>
    setFilter((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Box sx={{ p: 1 }} data-testid="cs-queue">
      <Title title="Kolejka CS" />
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sprawy CS według terminu (załadunek) i krytyczności: pilne — termin w ciągu 24 h, minął albo pilna uwaga
            FTL; wysokie — termin w ciągu 72 h albo zwrot z pytaniem (progi robocze).
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              mb: 1,
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
            }}
          >
            <TextField
              size="small"
              label="Klient"
              value={filter.client ?? ''}
              onChange={set('client')}
              slotProps={{ htmlInput: { 'data-testid': 'cs-queue-client' } }}
            />
            <TextField
              size="small"
              label="Lokalizacja"
              value={filter.location ?? ''}
              onChange={set('location')}
              helperText="kraj albo miejscowość"
              slotProps={{ htmlInput: { 'data-testid': 'cs-queue-location' } }}
            />
            <TextField
              select
              size="small"
              label="Rodzaj sprawy"
              value={filter.kind ?? ''}
              onChange={set('kind')}
              slotProps={{ htmlInput: { 'data-testid': 'cs-queue-kind' } }}
            >
              <MenuItem value="">wszystkie</MenuItem>
              {(queue?.kinds ?? []).map((k) => (
                <MenuItem key={k.id} value={k.id}>
                  {k.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Wykonawca"
              value={filter.executor ?? ''}
              onChange={set('executor')}
              slotProps={{ htmlInput: { 'data-testid': 'cs-queue-executor' } }}
            >
              <MenuItem value="">wszyscy</MenuItem>
              {(queue?.executors ?? []).map((x) => (
                <MenuItem key={x.id} value={x.id}>
                  {x.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {queue && queue.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="cs-queue-empty">
              Brak spraw w kolejce.
            </Typography>
          ) : null}
          {queue && queue.items.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Termin</TableCell>
                  <TableCell>Krytyczność</TableCell>
                  <TableCell>Sprawa</TableCell>
                  <TableCell>Klient</TableCell>
                  <TableCell>Lokalizacja</TableCell>
                  <TableCell>Wykonawca</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queue.items.map((c) => (
                  <TableRow key={c.key} data-testid={`cs-skrzynka-wiersz-${c.key}`} data-kind={c.kind}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.deadline ? dateTimeNoSeconds(c.deadline) : '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={c.criticalityReason}>
                        <Chip size="small" color={CRITICALITY_COLOR[c.criticality] ?? 'default'} label={c.criticalityLabel} />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {/* Kontrakt `data-testid` paczki (S-22.10-02): otwarcie sprawy — zmiana zlecenia albo prezlecenie. */}
                      <Link to={createPath({ resource: c.target.resource, type: 'show', id: c.target.id })} data-testid={caseLinkTestId(c)}>{c.title}</Link>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {c.kindLabel}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.clientName ?? '—'}</TableCell>
                    <TableCell>{c.location ?? '—'}</TableCell>
                    <TableCell>{c.executorLabel}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};
